
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, Clock, User, Phone, Mail, FileText, CheckCircle, XCircle, Trash2 } from "lucide-react";

interface AppointmentDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: any;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  onStatusUpdate?: (appointmentId: string, status: string) => void;
  onDelete?: (appointmentId: string) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

export const AppointmentDetailsDialog = ({
  open,
  onOpenChange,
  appointment,
  getStatusColor,
  getStatusText,
  onStatusUpdate,
  onDelete,
  isUpdating = false,
  isDeleting = false,
}: AppointmentDetailsDialogProps) => {
  if (!appointment) return null;

  const handleAccept = () => {
    if (onStatusUpdate) {
      onStatusUpdate(appointment.id, 'confirmed');
    }
  };

  const handleReject = () => {
    if (onStatusUpdate) {
      onStatusUpdate(appointment.id, 'cancelled');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      if (onDelete) {
        onDelete(appointment.id);
      }
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader className="bg-blue-600 text-white p-6 -m-6 mb-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl font-bold">Détails du Rendez-vous</DialogTitle>
              <p className="text-blue-100">ID: {appointment.id}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informations patient */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <User className="text-blue-600" size={20} />
              Informations Patient
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nom complet</p>
                <p className="font-medium">{appointment.patient?.full_name || "Patient inconnu"}</p>
              </div>
              {appointment.patient?.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Téléphone</p>
                    <p className="font-medium">{appointment.patient.phone}</p>
                  </div>
                </div>
              )}
              {appointment.patient?.email && (
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{appointment.patient.email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informations rendez-vous */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Calendar className="text-blue-600" size={20} />
              Détails du Rendez-vous
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Date demandée</p>
                <p className="font-medium">
                  {format(new Date(appointment.requested_date), "PPPp", { locale: fr })}
                </p>
              </div>
              {appointment.service && (
                <>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Service</p>
                      <p className="font-medium">{appointment.service.service_name}</p>
                    </div>
                  </div>
                  {appointment.service.duration && (
                    <div>
                      <p className="text-sm text-gray-600">Durée prévue</p>
                      <p className="font-medium">{appointment.service.duration} minutes</p>
                    </div>
                  )}
                  {appointment.service.description && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">Description du service</p>
                      <p className="font-medium">{appointment.service.description}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Notes et informations médicales */}
          {appointment.notes && (
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FileText className="text-green-600" size={20} />
                Notes de la demande
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-medium bg-white p-3 rounded border">{appointment.notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Statut actuel */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Statut de la demande</h3>
            <Badge className={`${getStatusColor(appointment.status)} px-4 py-2 text-sm font-medium`}>
              {getStatusText(appointment.status)}
            </Badge>
            <p className="text-sm text-gray-500 mt-2">
              Demande créée le {format(new Date(appointment.created_at), "PPPp", { locale: fr })}
            </p>
          </div>
        </div>

        {/* Boutons d'action */}
        {onStatusUpdate && onDelete && (
          <div className="border-t bg-gray-50 p-6 -m-6 mt-6 rounded-b-lg">
            <div className="flex flex-wrap gap-3 justify-end">
              {appointment.status === 'pending' && (
                <>
                  <Button
                    onClick={handleAccept}
                    disabled={isUpdating}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <CheckCircle size={18} />
                    {isUpdating ? 'En cours...' : 'Accepter'}
                  </Button>
                  
                  <Button
                    onClick={handleReject}
                    disabled={isUpdating}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <XCircle size={18} />
                    {isUpdating ? 'En cours...' : 'Refuser'}
                  </Button>
                </>
              )}
              
              <Button
                onClick={handleDelete}
                disabled={isDeleting}
                variant="outline"
                className="text-gray-600 hover:text-gray-700 hover:bg-gray-50 border-gray-300 px-6 py-3 flex items-center gap-2 shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Trash2 size={18} />
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
